//started

import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import { Input, FormGroup, Label } from "reactstrap";
import { useLocation } from "react-router-dom";
import {
  Canvas,
  Image as FabricImage,
  Textbox,
  Line,
  Rect,
  Circle,
  Ellipse,
  Triangle,
} from "fabric";
import Select, { components } from "react-select";
import { SketchPicker } from "react-color";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CreatableSelect from "react-select/creatable";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import CustomOption from "pages/Certificate/Templates/CustomOption";
import {
  CERTIFICATE_TYPE_OPTIONS,
  STROKE_WIDTH_OPTIONS,
  DASH_OPTIONS,
  PLACEHOLDER_OPTIONS,
  SIGNATURE_OPTIONS,
  SIGNATURE_DOCUMENT_MAP,
  FONT_SIZE_OPTIONS,
  FONT_OPTIONS,
  CERTIFICATE_SIZES,
} from "components/constant";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import AdminHeader from "components/Headers/AdminHeader";

const CertificateDesigner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const bgInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const shapeBtnRef = useRef(null);
  const textColorBtnRef = useRef(null);

  const [showShapePicker, setShowShapePicker] = useState(false);
  const [activeObject, setActiveObject] = useState(null);
  const [fontSize, setFontSize] = useState("");
  // text color picker (fa-palette)
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);

  // shape color picker
  const [shapeColorPicker, setShapeColorPicker] = useState({
    type: null, // "stroke" | "fill"
    top: 0,
    left: 0,
  });
  const [copyToast, setCopyToast] = useState({
    visible: false,
    text: "",
    top: 0,
    left: 0,
  });
  const [hasBackground, setHasBackground] = useState(false);
  const [selectedFont, setSelectedFont] = useState();
  const [shapeToolbar, setShapeToolbar] = useState({
    visible: false,
    top: 0,
    left: 0,
  });
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [editingTextObject, setEditingTextObject] = useState(null);
  const [isDynamicFieldOpen, setIsDynamicFieldOpen] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showStartModal, setShowStartModal] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    if (fabricCanvasRef.current) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const canvas = new Canvas(canvasEl, {
      width: 900,
      height: 600,
      selection: true,
    });

    fabricCanvasRef.current = canvas;

    const fromView = location.state?.fromView;
    const saved = localStorage.getItem("currentTemplate");

    if (!fromView) {
      localStorage.removeItem("currentTemplate");
    }

    if (fromView && saved) {
      const template = JSON.parse(saved);

      canvas.setDimensions({
        width: template.canvasSize.width,
        height: template.canvasSize.height,
      });

      setSelectedSize("custom");
      setShowStartModal(false);
      setHasBackground(true);

      const bgUrl =
        template.backgroundType === "image"
          ? require("assets/img/certificates_templates/certificate3.jpg")
          : require("assets/img/certificates_templates/blank.png");

      FabricImage.fromURL(bgUrl).then((bg) => {
        bg.set({
          originX: "center",
          originY: "center",
          left: canvas.width / 2,
          top: canvas.height / 2,
          selectable: false,
          evented: false,
        });

        bg.scaleToWidth(canvas.width);
        bg.scaleToHeight(canvas.height);

        canvas.backgroundImage = bg;
        canvas.renderAll();
      });

      template.layout.forEach((item) => {
        const left = (item.x / 100) * canvas.width;
        const top = (item.y / 100) * canvas.height;

        if (item.type === "text") {
          const text = new Textbox(item.text, {
            left,
            top,
            fontSize: item.fontSize,
            fontFamily: item.fontFamily,
            fill: item.color,
            angle: item.angle || 0,
          });

          text.custom = { type: "text" };
          canvas.add(text);
        }

        if (item.type === "shape") {
          let shape;

          switch (item.shapeType) {
            case "rect":
            case "triangle":
              shape = new Rect({
                left,
                top,
                width: item.width || 100,
                height: item.height || 100,
                stroke: item.stroke,
                fill: item.fill,
                strokeWidth: item.strokeWidth || 2,
              });
              break;

            case "circle":
              shape = new Circle({
                left,
                top,
                radius: item.radius,
                stroke: item.stroke,
                fill: item.fill,
                strokeWidth: item.strokeWidth || 2,
              });
              break;

            case "ellipse":
              shape = new Ellipse({
                left,
                top,
                rx: item.rx,
                ry: item.ry,
                stroke: item.stroke,
                fill: item.fill,
                strokeWidth: item.strokeWidth || 2,
              });
              break;

            case "line":
              shape = new Line([item.x1, item.y1, item.x2, item.y2], {
                stroke: item.stroke,
                strokeWidth: item.strokeWidth || 2,
              });
              break;

            default:
              shape = new Rect({
                left,
                top,
                width: item.width || 100,
                height: item.height || 100,
                stroke: item.stroke,
                fill: item.fill,
              });
          }

          shape.custom = { type: "shape" };
          canvas.add(shape);
        }

        if (item.type === "qr") {
          if (item.src) {
            FabricImage.fromURL(item.src).then((img) => {
              img.set({
                left,
                top,
                scaleX: item.scaleX || 1,
                scaleY: item.scaleY || 1,
                originX: "center",
                originY: "center",
              });

              img.custom = { type: "qr", src: item.src };
              canvas.add(img);
              canvas.renderAll();
            });
          } else {
            // fallback placeholder
            const rect = new Rect({
              left,
              top,
              width: item.width || 120,
              height: item.height || 120,
              fill: "#f5f5f5",
              stroke: "#666",
              strokeDashArray: [6, 4],
              originX: "center",
              originY: "center",
            });

            rect.custom = { type: "qr" };
            canvas.add(rect);
          }
        }

        if (item.type === "signature") {
          FabricImage.fromURL(item.src).then((img) => {
            img.set({
              left,
              top,
              scaleX: item.scaleX || 1,
              scaleY: item.scaleY || 1,
              angle: item.angle || 0,
            });

            img.custom = {
              type: "signature",
              signatureKey: item.signatureKey,
              src: item.src,
            };

            canvas.add(img);
            canvas.renderAll();
          });
        }

        if (item.type === "image") {
          FabricImage.fromURL(item.src).then((img) => {
            img.set({
              left,
              top,
              scaleX: item.width / img.width,
              scaleY: item.height / img.height,
            });

            img.custom = { type: "image", src: item.src };
            canvas.add(img);
            canvas.renderAll();
          });
        }
      });

      canvas.renderAll();
    }

    const updateToolbarOnTransform = (e) => {
      const obj = e.target;
      if (!obj || obj.custom?.type !== "shape") return;

      setShapeToolbar({
        visible: true,
        ...getShapeToolbarPosition(obj),
      });
    };

    canvas.on("object:moving", updateToolbarOnTransform);
    canvas.on("object:scaling", (e) => {
      const obj = e.target;

      if (obj?.type !== "textbox") return;

      // Convert scaleY into height
      const newHeight = obj.height * obj.scaleY;

      obj.set({
        height: newHeight,
        scaleY: 1,
      });

      obj.setCoords();
      canvas.requestRenderAll();
    });

    canvas.on("object:rotating", updateToolbarOnTransform);

    canvas.on("selection:created", (e) => {
      const obj = e.selected?.[0];
      if (!obj) return;

      setActiveObject(obj);
      setFontSize(obj?.fontSize || "");
      setShapeColorPicker({ type: null, top: 0, left: 0 });
      setTextColorPickerOpen(false);

      if (obj.custom?.type === "shape") {
        setShapeToolbar({
          visible: true,
          ...getShapeToolbarPosition(obj),
        });
      }
    });

    canvas.on("selection:updated", (e) => {
      const obj = e.selected?.[0];
      if (!obj) return;

      setActiveObject(obj);

      if (obj.custom?.type === "shape") {
        setShapeToolbar({
          visible: true,
          ...getShapeToolbarPosition(obj),
        });
      }
    });

    canvas.on("selection:cleared", () => {
      setActiveObject(null);
      setFontSize("");
      setShapeToolbar({ visible: false, top: 0, left: 0 });
      setEditingTextObject(null);
    });

    canvas.on("text:editing:entered", (e) => {
      setEditingTextObject(e.target);
    });

    canvas.on("text:editing:exited", () => {
      setEditingTextObject(null);
    });

    const handleKeyDown = (e) => {
      const active = canvas.getActiveObject();
      if (active && active.isEditing) return;
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // localStorage.removeItem("currentTemplate");
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, []);

  const fitBackgroundToCanvas = (canvas) => {
    if (!canvas || !canvas.backgroundImage) return;

    const bg = canvas.backgroundImage;

    const { width, height } = canvas;

    bg.set({
      originX: "center",
      originY: "center",
      left: width / 2,
      top: height / 2,
    });

    // Force exact fit (cover the whole canvas)
    bg.scaleX = width / bg.width;
    bg.scaleY = height / bg.height;

    bg.setCoords();
  };

  const applyCanvasSize = (sizeKey) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const { width, height } = CERTIFICATE_SIZES[sizeKey];

    canvas.setDimensions({ width, height });

    fitBackgroundToCanvas(canvas);

    canvas.requestRenderAll();

    setSelectedSize(sizeKey);
    setShowStartModal(false);
  };

  const addQrPlaceholder = () => {
    const canvas = fabricCanvasRef.current;

    const rect = new Rect({
      left: canvas.width / 2,
      top: canvas.height / 2,
      width: 120,
      height: 120,
      fill: "#f5f5f5",
      stroke: "#666",
      strokeWidth: 2,
      strokeDashArray: [6, 4],
      originX: "center",
      originY: "center",
      selectable: true,
    });

    rect.custom = {
      type: "qr",
      role: "placeholder",
    };

    canvas.add(rect);
    canvas.setActiveObject(rect);
  };

  const addQrImage = async (file) => {
    const canvas = fabricCanvasRef.current;
    const active = canvas.getActiveObject();

    if (!active || active.custom?.type !== "qr") return;

    const url = URL.createObjectURL(file);
    const img = await FabricImage.fromURL(url);

    // Scale image to fit square
    const scale = Math.max(
      active.width / img.width,
      active.height / img.height,
    );

    img.set({
      left: active.left,
      top: active.top,
      scaleX: scale,
      scaleY: scale,
      originX: "center",
      originY: "center",
      clipPath: active,
    });

    img.custom = {
      type: "qr",
      src: url,
    };

    canvas.remove(active); // remove placeholder rect
    canvas.add(img);
    canvas.setActiveObject(img);
    canvas.renderAll();
  };

  const getShapeToolbarPosition = (obj) => {
    const canvas = fabricCanvasRef.current;

    // IMPORTANT: use upperCanvasEl (actual visible canvas)
    const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();

    // aCoords are already transformed & zoomed
    const { tr } = obj.aCoords; // top-right

    return {
      top: canvasRect.top + tr.y,
      left: canvasRect.left + tr.x,
    };
  };

  const loadBlankBackground = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !selectedSize) {
      showAlert({
        type: "warning",
        title: "Select Certificate Size",
        message: "Please select certificate size first.",
      });
      return;
    }

    const url = require("assets/img/certificates_templates/blank.png");
    const bg = await FabricImage.fromURL(url);

    bg.set({
      originX: "center",
      originY: "center",
      left: canvas.width / 2,
      top: canvas.height / 2,
      selectable: false,
      evented: false,
    });

    bg.scaleToWidth(canvas.width);
    bg.scaleToHeight(canvas.height);

    canvas.backgroundImage = bg;
    canvas.requestRenderAll();

    setHasBackground(true);
    toggleCanvasBorder(true);
    setShowStartModal(false);
  };

  const addSignatureImage = async (signatureKey) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const url = SIGNATURE_DOCUMENT_MAP[signatureKey];
    if (!url) return;

    const img = await FabricImage.fromURL(url);

    img.set({
      left: 200,
      top: 200,
      scaleX: 0.5,
      scaleY: 0.5,
    });

    img.custom = {
      type: "signature",
      signatureKey,
      src: url,
    };

    canvas.add(img);
    canvas.setActiveObject(img);
    canvas.requestRenderAll();
  };

  const addBackgroundImage = async (e) => {
    if (!selectedSize) {
      showAlert({
        type: "warning",
        title: "Select Certificate Size",
        message: "Please select certificate size first.",
      });
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    const canvas = fabricCanvasRef.current;
    const url = URL.createObjectURL(file);
    const bg = await FabricImage.fromURL(url);

    bg.set({
      originX: "center",
      originY: "center",
      left: canvas.width / 2,
      top: canvas.height / 2,
      selectable: false,
      evented: false,
    });

    bg.scaleToWidth(canvas.width);
    bg.scaleToHeight(canvas.height);

    canvas.backgroundImage = bg;
    fitBackgroundToCanvas(canvas);
    canvas.requestRenderAll();

    setHasBackground(true);
    toggleCanvasBorder(true);
    setShowStartModal(false);

    e.target.value = "";
  };

  const addText = () => {
    const canvas = fabricCanvasRef.current;

    const text = new Textbox("Sample Text", {
      left: 120,
      top: 120,
      fontSize: 24,
      fill: "#000",
      fontFamily: "Arial",
      lockUniScaling: false,
      splitByGrapheme: false,
    });

    text.custom = { type: "text" };
    canvas.add(text);
    canvas.setActiveObject(text);
  };

  const addImage = async (e) => {
    const file = e.target.files[0];
    const src = require("assets/img/certificates_templates/certificate3.jpg");
    if (!file) return;

    const url = URL.createObjectURL(file);
    const canvas = fabricCanvasRef.current;

    const img = await FabricImage.fromURL(url);
    img.scaleToWidth(150);
    img.left = 200;
    img.top = 200;

    img.custom = { type: "image", src };
    canvas.add(img);
    canvas.setActiveObject(img);
  };

  const addShape = (shapeType) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    let shape;

    switch (shapeType) {
      case "line":
        shape = new Line([100, 100, 300, 100], {
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "line" };
        break;

      case "rect":
        shape = new Rect({
          left: 150,
          top: 150,
          width: 200,
          height: 120,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "circle":
        shape = new Circle({
          left: 200,
          top: 200,
          radius: 60,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "ellipse":
        shape = new Ellipse({
          left: 200,
          top: 200,
          rx: 80,
          ry: 40,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "triangle":
        shape = new Triangle({
          left: 200,
          top: 200,
          width: 120,
          height: 120,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;
    }

    if (shape) {
      canvas.add(shape);
      canvas.setActiveObject(shape);
    }
  };

  const openTemplateMetaAlert = (onSubmit) => {
    let templateType = null;
    let templateName = "";

    showAlert({
      title: "Create Certificate Template",
      confirmText: "Create",
      cancelText: "Cancel",
      confirmStyle: "success",
      cancelStyle: "secondary",
      showCancel: true,
      closeOnConfirm: false,
      message: (
        <>
          <FormGroup className="mt-3">
            <Label className="small font-weight-bold">
              Certificate Template Name
            </Label>
            <Input
              type="text"
              placeholder="Enter certificate template name"
              onChange={(e) => (templateName = e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label className="small font-weight-bold">
              Certificate Template Type
            </Label>
            <Select
              options={CERTIFICATE_TYPE_OPTIONS}
              placeholder="Select certificate type"
              styles={fixedWidthCustomStyle("14rem")}
              menuPortalTarget={document.body}
              onChange={(val) => (templateType = val)}
            />
          </FormGroup>
        </>
      ),
      onConfirm: (closeAlert) => {
        if (!templateName?.trim() || !templateType) return;

        onSubmit({
          templateName: templateName.trim(),
          templateType: templateType.value,
        });

        closeAlert();
      },
    });
  };

  const updateStyle = (style, value) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const obj = canvas.getActiveObject();
    if (!obj) return;

    obj.set(style, value);

    obj.set("dirty", true);

    canvas.requestRenderAll();

    canvas.fire("object:modified", { target: obj });
  };

  const toggleCanvasBorder = (show) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvas.upperCanvasEl) return;

    canvas.upperCanvasEl.classList.toggle("blank-canvas-border", show);
  };

  const deleteSelectedObject = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Delete selected object directly
    if (activeObject) {
      canvas.remove(activeObject);
      setActiveObject(null);
      canvas.renderAll();
      return;
    }

    // Background delete confirmation
    if (canvas.backgroundImage) {
      showAlert({
        type: "warning",
        title: "Remove Background?",
        message: "This will remove the certificate background image.",
        confirmText: "Yes, remove",
        cancelText: "Cancel",
        confirmStyle: "danger",
        cancelStyle: "secondary",
        showCancel: true,
        onConfirm: () => {
          setShapeToolbar({ visible: false, top: 0, left: 0 });
          setShapeColorPicker({ type: null, top: 0, left: 0 });
          setTextColorPickerOpen(false);
          setShowAlignMenu(false);

          canvas.discardActiveObject();
          canvas.backgroundImage = null;
          canvas.renderAll();

          setHasBackground(false);
          toggleCanvasBorder(false);
        },
      });
    }
  };

  const copyDynamicField = async (value, label, event) => {
    if (value === "qr_code") {
      addQrPlaceholder();
      return;
    }

    const placeholder = `{{${value}}}`;

    // Read the bounding rect IMMEDIATELY (synchronously)
    const rect = event.currentTarget.getBoundingClientRect();

    try {
      await navigator.clipboard.writeText(placeholder);

      const toastWidth = 90;

      setCopyToast({
        visible: true,
        text: `${label} copied`,
        top: rect.top + rect.height / 2,
        left: rect.left - toastWidth - 8,
      });

      // Hide after 1.5s using functional update to avoid stale state
      setTimeout(() => {
        setCopyToast((prev) => ({ ...prev, visible: false }));
      }, 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
      // Optional: show error toast here if needed
    }
  };

  const buildTemplateFromCanvas = ({
    name = "Unsaved Template",
    templateType = null,
  } = {}) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;

    const layout = canvas.getObjects().map((obj) => {
      const base = {
        type: obj.custom?.type,
        x: (obj.left / canvas.width) * 100,
        y: (obj.top / canvas.height) * 100,
      };

      switch (obj.custom?.type) {
        case "text":
          return {
            ...base,
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            color: obj.fill,
            angle: obj.angle || 0,
            textAlign: obj.textAlign || "left",
            originX: obj.originX || "center",
            originY: obj.originY || "center",
            width: obj.width || null,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            isPlaceholder: false,
          };

        case "qr":
          return {
            ...base,
            type: "qr",
            src: obj.custom.src || null,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
            angle: obj.angle || 0,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
          };

        case "image":
          return {
            ...base,
            src: obj.custom.src,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
          };

        case "signature":
          return {
            ...base,
            type: "signature",
            src: obj.custom.src,
            signatureKey: obj.custom.signatureKey,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
            angle: obj.angle || 0,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
          };

        case "shape": {
          const shapeBase = {
            ...base,
            shapeType: obj.type,
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            fill: obj.fill,
            dash: obj.strokeDashArray || [],
            angle: obj.angle || 0,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
          };

          switch (obj.type) {
            case "ellipse":
              return {
                ...shapeBase,
                rx: obj.rx,
                ry: obj.ry,
              };

            case "circle":
              return {
                ...shapeBase,
                radius: obj.radius,
              };

            case "rect":
            case "triangle":
              return {
                ...shapeBase,
                width: obj.width,
                height: obj.height,
              };

            case "line":
              return {
                ...shapeBase,
                x1: obj.x1,
                y1: obj.y1,
                x2: obj.x2,
                y2: obj.y2,
              };

            default:
              return shapeBase;
          }
        }

        default:
          return base;
      }
    });

    return {
      id: Date.now(),
      name,
      templateType,
      previewImage: "/certificate-bg.png",
      backgroundType: hasBackground ? "image" : "blank",
      canvasSize: {
        width: canvas.width,
        height: canvas.height,
      },
      createdAt: new Date().toLocaleDateString(),
      layout,
    };
  };

  const handleSave = () => {
    openTemplateMetaAlert(({ templateType, templateName }) => {
      const newTemplate = buildTemplateFromCanvas({
        name: templateName,
        templateType,
      });

      if (!newTemplate) return;
    });
  };

  const handleViewTemplate = () => {
    const runtimeTemplate = buildTemplateFromCanvas();

    if (!runtimeTemplate) return;

    localStorage.setItem("currentTemplate", JSON.stringify(runtimeTemplate));
    sessionStorage.setItem("fromDesignerToView", "true");

    navigate("/admin/certificate/view", {
      state: {
        template: runtimeTemplate,
      },
    });
  };

  const handleAddShape = (shapeType) => {
    addShape(shapeType);
    setShowShapePicker(false);
  };

  const handleTextStyleToggle = (styleType) => {
    if (!activeObject) return;

    switch (styleType) {
      case "bold": {
        const isBold = activeObject.fontWeight === "bold";
        updateStyle("fontWeight", isBold ? "normal" : "bold");
        break;
      }

      case "italic": {
        const isItalic = activeObject.fontStyle === "italic";
        updateStyle("fontStyle", isItalic ? "normal" : "italic");
        break;
      }

      case "underline": {
        const isUnderline = activeObject.underline === true;
        updateStyle("underline", !isUnderline);
        break;
      }

      default:
        break;
    }
  };

  const handleOpenShapeColorPicker = (e, type) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setShapeColorPicker({
      type,
      top: rect.top + window.scrollY,
      left: rect.right + 8,
    });
  };

  const handleTextColorPickerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setTextColorPickerOpen((prev) => !prev);
    setShapeColorPicker({
      type: "text",
      top: rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
    });
  };

  const FontOption = (props) => {
    return (
      <components.Option {...props}>
        <span style={{ fontFamily: props.data.value }}>{props.data.label}</span>
      </components.Option>
    );
  };

  const FontSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <span style={{ fontFamily: props.data.value }}>{props.data.label}</span>
      </components.SingleValue>
    );
  };

  const WeightSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        {props.children}
      </components.SingleValue>
    );
  };

  const DashSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        {props.children}
      </components.SingleValue>
    );
  };

  const selectStylesWithHighZ = {
    ...fixedWidthCustomStyle("9rem"),
    menuPortal: (base) => ({
      ...base,
      zIndex: 8000, // higher than dynamic-field-panel (6000)
    }),
  };

  const resetDesigner = () => {
    localStorage.removeItem("currentTemplate");
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    canvas.clear();
    canvas.requestRenderAll();

    setActiveObject(null);
    setHasBackground(false);
    setTextColorPickerOpen(false);
    setShowAlignMenu(false);
    setShapeToolbar({ visible: false, top: 0, left: 0 });
    setShapeColorPicker({ type: null, top: 0, left: 0 });

    toggleCanvasBorder(false);
  };

  const handleBack = () => {
    if (hasBackground) {
      resetDesigner();
    } else {
      navigate(-1);
    }
  };

  const applyZoom = (newZoom) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));

    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, clampedZoom);

    setZoom(clampedZoom);
    canvas.requestRenderAll();
  };

  const ShapeButton = ({ onClick, children, label }) => (
    <div className="shape-btn" onClick={onClick} title={label}>
      {children}
    </div>
  );

  const handleCloseStartModal = () => {
    setShowStartModal(false);
    navigate("/admin/certificate/templates");
  };

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.1;
  const zoomIn = () => applyZoom(zoom + ZOOM_STEP);
  const zoomOut = () => applyZoom(zoom - ZOOM_STEP);

  const isShape = activeObject?.custom?.type === "shape";
  const isLine = activeObject?.type === "line";
  const isClosedShape = isShape && !isLine;

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i
                      className="fa fa-chevron-left mr-3 cursor"
                      onClick={handleBack}
                    />
                    <span className="font-weight-600">Template Designer</span>
                  </div>
                  <div className="d-fles justify-content-right align-items-center">
                    <Button
                      variant="primary"
                      className="btn-deep-blue"
                      onClick={handleViewTemplate}
                    >
                      View Template
                    </Button>

                    <Button variant="success" onClick={handleSave}>
                      Save Layout
                    </Button>
                  </div>
                </div>
              </Card.Header>

              <Card.Body>
                <Row className="mb-3 align-items-center justify-content-center">
                  <Col md="auto" className="mr--3">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden-file-input"
                      onChange={addImage}
                    />

                    <Button
                      className="mt-3 btn-white btn-outline-primary-300"
                      color="default"
                      onClick={() => {
                        imageInputRef.current.value = "";
                        imageInputRef.current.click();
                      }}
                    >
                      <i className="fa fa-image mr-1" />
                      Add Image
                    </Button>
                  </Col>

                  <Col md="auto" className="custom-wrapper mr--3">
                    <Button
                      ref={shapeBtnRef}
                      className="mt-3 btn-white btn-outline-primary-300"
                      onClick={() => setShowShapePicker((v) => !v)}
                    >
                      <i className="fa fa-shapes mr-1" />
                      Shapes
                    </Button>

                    {showShapePicker && (
                      <div className="shape-picker">
                        <div className="shape-grid">
                          {/* Line */}
                          <ShapeButton
                            label="Line"
                            onClick={() => handleAddShape("line")}
                          >
                            <svg width="36" height="36">
                              <line
                                x1="4"
                                y1="18"
                                x2="32"
                                y2="18"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Rectangle */}
                          <ShapeButton
                            label="Rectangle"
                            onClick={() => handleAddShape("rect")}
                          >
                            <svg width="36" height="36">
                              <rect
                                x="6"
                                y="8"
                                width="24"
                                height="18"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Circle */}
                          <ShapeButton
                            label="Circle"
                            onClick={() => handleAddShape("circle")}
                          >
                            <svg width="36" height="36">
                              <circle
                                cx="18"
                                cy="18"
                                r="10"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Ellipse */}
                          <ShapeButton
                            label="Ellipse"
                            onClick={() => handleAddShape("ellipse")}
                          >
                            <svg width="36" height="36">
                              <ellipse
                                cx="18"
                                cy="18"
                                rx="12"
                                ry="8"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Triangle */}
                          <ShapeButton
                            label="Triangle"
                            onClick={() => handleAddShape("triangle")}
                          >
                            <svg width="36" height="36">
                              <polygon
                                points="18,6 30,30 6,30"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>
                        </div>
                      </div>
                    )}
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Button
                      color="default"
                      onClick={addText}
                      className="btn-white btn-outline-primary-300 mt-3"
                    >
                      Add Text
                    </Button>
                  </Col>

                  {/* Text Alignment (Compact) */}
                  <Col md="auto" className="custom-wrapper mr--3">
                    <Button
                      className="mt-3 btn-white btn-outline-primary-300"
                      onClick={() => setShowAlignMenu((v) => !v)}
                      title="Text alignment"
                      // style={{ lineHeight: 1 }}
                    >
                      <i className="fa fa-align-left" />
                      <i class="fa-solid fa-caret-down"></i>
                    </Button>

                    {showAlignMenu && (
                      <div className="align-menu">
                        {[
                          {
                            icon: "align-left",
                            label: "Align Left",
                            value: "left",
                          },
                          {
                            icon: "align-center",
                            label: "Align Center",
                            value: "center",
                          },
                          {
                            icon: "align-right",
                            label: "Align Right",
                            value: "right",
                          },
                          {
                            icon: "align-justify",
                            label: "Justify",
                            value: "justify",
                          },
                        ].map((opt) => (
                          <div
                            key={opt.value}
                            className="align-menu-item"
                            onClick={() => {
                              updateStyle("textAlign", opt.value);
                              setShowAlignMenu(false);
                            }}
                          >
                            <i className={`fa fa-${opt.icon}`} />
                            <span>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>

                  <Col md="auto" className="custom-wrapper mr--3">
                    <Button
                      ref={textColorBtnRef}
                      className="mt-3 btn-white btn-outline-primary-300"
                      onClick={handleTextColorPickerClick}
                    >
                      <i className="fa fa-palette fa-lg" />
                    </Button>

                    {textColorPickerOpen &&
                      createPortal(
                        <div
                          className="text-color-picker"
                          style={{
                            "--picker-top": `${shapeColorPicker.top}px`,
                            "--picker-left": `${shapeColorPicker.left}px`,
                          }}
                        >
                          <SketchPicker
                            color={
                              activeObject?.custom?.type === "text"
                                ? activeObject.fill || "#000"
                                : "#000"
                            }
                            onChangeComplete={(color) => {
                              if (
                                !activeObject ||
                                activeObject.custom?.type !== "text"
                              )
                                return;
                              updateStyle("fill", color.hex);
                            }}
                          />
                        </div>,
                        document.body,
                      )}
                  </Col>

                  <Col md="auto" className="mr--3">
                    <ButtonGroup className="mt-3 layout-height">
                      <Button
                        className="btn-white btn-outline-primary-300"
                        size="sm"
                        onClick={() => handleTextStyleToggle("bold")}
                      >
                        <i className="fa-solid fa-bold" />
                      </Button>

                      <Button
                        className="btn-white btn-outline-primary-300"
                        size="sm"
                        onClick={() => handleTextStyleToggle("italic")}
                      >
                        <i className="fa-solid fa-italic" />
                      </Button>

                      <Button
                        className="btn-white btn-outline-primary-300"
                        size="sm"
                        onClick={() => handleTextStyleToggle("underline")}
                      >
                        <i className="fa-solid fa-underline" />
                      </Button>
                    </ButtonGroup>
                  </Col>

                  <Col md="auto" className="mr--3">
                    <ButtonGroup className="mt-3 layout-height">
                      <Button
                        size="sm"
                        className="btn-white btn-outline-primary-300"
                        onClick={zoomOut}
                      >
                        <i class="fa-solid fa-minus" />
                      </Button>
                      <Button
                        size="sm"
                        className="btn btn-outline-primary-300"
                        disabled
                        variant=""
                      >
                        {Math.round(zoom * 100)}%
                      </Button>
                      <Button
                        size="sm"
                        className="btn-white btn-outline-primary-300"
                        onClick={zoomIn}
                      >
                        <i class="fa-solid fa-plus" />
                      </Button>
                    </ButtonGroup>
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Select
                      className="mt-3"
                      options={SIGNATURE_OPTIONS}
                      placeholder="Insert signature"
                      components={{
                        Option: CustomOption,
                      }}
                      classNamePrefix="react-select"
                      styles={{
                        ...fixedWidthCustomStyle(),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      onChange={(selected) => {
                        addSignatureImage(selected.value);
                      }}
                    />
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Select
                      className="mt-3"
                      options={FONT_OPTIONS}
                      value={selectedFont}
                      placeholder="Select font"
                      components={{
                        Option: FontOption,
                        SingleValue: FontSingleValue,
                      }}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle("11rem")}
                      menuPortalTarget={document.body}
                      onChange={(selected) => {
                        setSelectedFont(selected);
                        updateStyle("fontFamily", selected.value);
                      }}
                      isSearchable
                    />
                  </Col>

                  <Col md="auto" className="mr--3">
                    <CreatableSelect
                      className="mt-3"
                      placeholder="Font size"
                      value={
                        fontSize
                          ? { value: fontSize, label: String(fontSize) }
                          : null
                      }
                      options={FONT_SIZE_OPTIONS}
                      styles={selectStylesWithHighZ}
                      menuPortalTarget={document.body}
                      classNamePrefix="react-select"
                      // isDisabled={!activeObject || !activeObject.fontSize}
                      onChange={(selected) => {
                        const size = Number(selected.value);
                        setFontSize(size);
                        updateStyle("fontSize", size);
                      }}
                      onCreateOption={(inputValue) => {
                        const size = Number(inputValue);
                        if (!isNaN(size) && size > 0) {
                          setFontSize(size);
                          updateStyle("fontSize", size);
                        }
                      }}
                    />
                  </Col>

                  <Col md="auto">
                    <Button
                      className="mt-3"
                      variant="danger"
                      // disabled={!activeObject && !hasBackground}
                      onClick={deleteSelectedObject}
                    >
                      Delete
                    </Button>
                  </Col>
                </Row>

                {/* Canvas */}
                <Row className="justify-content-start align-items-start">
                  <Col md={1} className="d-flex justify-content-start pt-4">
                    <div
                      className="instructions-info-btn"
                      onClick={() => setShowInstructions(true)}
                      title="View instructions"
                    >
                      <i className="fa-solid fa-circle-info" />
                    </div>
                  </Col>

                  <Col md={7} className="canvas-wrapper">
                    <div className="bg-upload-overlay-wrapper">
                      {!hasBackground && (
                        <div className="bg-upload-overlay">
                          <div className="layout-card mb-3 px-4 py-4">
                            <p className="bg-upload-title">
                              Upload Template Background
                            </p>

                            <input
                              ref={bgInputRef}
                              type="file"
                              accept="image/*"
                              className="d-none"
                              onChange={addBackgroundImage}
                            />

                            <div
                              className="bg-upload-icon"
                              onClick={() => bgInputRef.current.click()}
                            >
                              <i className="fa-solid fa-upload" />
                            </div>
                          </div>
                          <div
                            className="layout-card px-4 py-4"
                            onClick={async () => {
                              await loadBlankBackground();
                              setShowStartModal(false);
                            }}
                          >
                            <i className="fa-regular fa-file" />
                            <h4>Blank Layout</h4>
                            <p>Start with an empty certificate</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <canvas ref={canvasRef} key="fabric-canvas" />
                  </Col>
                </Row>

                {shapeToolbar.visible &&
                  isShape &&
                  createPortal(
                    <div
                      className="shape-toolbar"
                      style={{
                        "--toolbar-top": `${shapeToolbar.top}px`,
                        "--toolbar-left": `${shapeToolbar.left}px`,
                      }}
                    >
                      {/* Stroke Width */}
                      <Select
                        options={STROKE_WIDTH_OPTIONS}
                        placeholder="Weight"
                        menuPlacement="top"
                        components={{ SingleValue: WeightSingleValue }}
                        styles={{
                          ...fixedWidthCustomStyle(),
                          container: (b) => ({ ...b, width: "100%" }),
                        }}
                        value={
                          STROKE_WIDTH_OPTIONS.find(
                            (opt) => opt.value === activeObject?.strokeWidth,
                          ) || null
                        }
                        onChange={(opt) =>
                          updateStyle("strokeWidth", opt.value)
                        }
                      />

                      {/* Dash Style */}
                      <Select
                        options={DASH_OPTIONS}
                        placeholder="Dash"
                        menuPlacement="top"
                        components={{ SingleValue: DashSingleValue }}
                        styles={{
                          ...fixedWidthCustomStyle(),
                          container: (b) => ({ ...b, width: "100%" }),
                        }}
                        value={
                          DASH_OPTIONS.find(
                            (opt) =>
                              JSON.stringify(opt.value) ===
                              JSON.stringify(
                                activeObject?.strokeDashArray || [],
                              ),
                          ) || DASH_OPTIONS[0]
                        }
                        onChange={(opt) =>
                          updateStyle("strokeDashArray", opt.value)
                        }
                      />

                      {/* Colors row */}
                      <div className="shape-toolbar-colors">
                        {/* Border color */}
                        <Button
                          size="sm"
                          variant="light"
                          className="shape-color-btn"
                          onClick={(e) =>
                            handleOpenShapeColorPicker(e, "stroke")
                          }
                          title="Border color"
                        >
                          <i className="fa-solid fa-border-all" />
                        </Button>

                        {/* Fill color (closed shapes only) */}
                        {isClosedShape && (
                          <Button
                            size="sm"
                            variant="light"
                            className="shape-color-btn"
                            onClick={(e) =>
                              handleOpenShapeColorPicker(e, "fill")
                            }
                            title="Fill color"
                          >
                            <i className="fa-solid fa-fill-drip" />
                          </Button>
                        )}
                      </div>
                    </div>,
                    document.body,
                  )}
              </Card.Body>
              {shapeColorPicker.type &&
                activeObject &&
                createPortal(
                  <div
                    className="shape-color-picker"
                    style={{
                      "--picker-top": `${shapeColorPicker.top}px`,
                      "--picker-left": `${shapeColorPicker.left}px`,
                    }}
                  >
                    <SketchPicker
                      color={
                        shapeColorPicker.type === "stroke"
                          ? activeObject.stroke || "#000"
                          : activeObject.fill || "#ffffff"
                      }
                      onChangeComplete={(color) => {
                        updateStyle(
                          shapeColorPicker.type === "stroke"
                            ? "stroke"
                            : "fill",
                          color.hex,
                        );
                      }}
                    />
                  </div>,
                  document.body,
                )}
            </Card>
          </Col>
        </Row>

        {createPortal(
          <div
            className={`dynamic-field-panel ${
              isDynamicFieldOpen ? "open" : "collapsed"
            }`}
          >
            {/* HEADER (clickable) */}
            <div
              className="df-header df-toggle"
              onClick={() => setIsDynamicFieldOpen((v) => !v)}
            >
              <i
                className={`fa-solid ${
                  isDynamicFieldOpen ? "fa-chevron-down" : "fa-chevron-up"
                }`}
              />
              <span>Dynamic Fields</span>
            </div>

            {/* BODY */}
            {isDynamicFieldOpen && (
              <div className="df-body">
                {PLACEHOLDER_OPTIONS.map((p) => (
                  <button
                    key={p.value}
                    className="df-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyDynamicField(p.value, p.label, e);
                    }}
                  >
                    <span>{p.label}</span>
                    {p.value !== "qr_code" && (
                      <i className="fa-solid fa-copy" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}

        {createPortal(
          <div
            className={`instructions-panel ${
              showInstructions ? "open" : "closed"
            }`}
          >
            {/* Header */}
            <div className="instructions-header">
              <span>Instructions</span>
              <i
                className="fa-solid fa-xmark"
                onClick={() => setShowInstructions(false)}
              />
            </div>

            {/* Body */}
            <div className="instructions-body">
              <ol className="instructions-list">
                <li>Upload a certificate background image</li>
                <li>
                  Click <b>Add Text</b> to insert text
                </li>
                <li>
                  Use <b>Dynamic Fields</b> for placeholders
                </li>
                <li>Drag elements to reposition them</li>
                <li>Use toolbar to style text and shapes</li>
                <li>
                  Click <b>Save Layout</b> after finishing
                </li>
              </ol>

              <small className="text-muted">
                Tip: Double-click text to edit inline
              </small>
            </div>
          </div>,
          document.body,
        )}

        {showStartModal &&
          createPortal(
            <div className="start-layout-overlay">
              <div className="start-layout-modal">
                <div className="d-flex justify-content-end">
                  <Button
                    className="btn btn-white no-shadow"
                    onClick={handleCloseStartModal}
                    color="default"
                    title="Close"
                  >
                    <i className="fa-solid fa-xmark cursor text-muted" />
                  </Button>
                </div>
                <h2>Select Certificate Size</h2>

                <div className="layout-options">
                  {/* PLEASE NOTE:  we have to work on A4 and A3 in future PR's */}
                  <div className="d-flex justify-content-center">
                    <div
                      className="layout-card mr-5 px-5 py-5 shadow-lg"
                      onClick={() => applyCanvasSize("A4")}
                    >
                      <i className="fa-regular fa-file" />
                      <h2>A4</h2>
                    </div>

                    <div
                      className="layout-card px-5 py-5 shadow-lg"
                      onClick={() => applyCanvasSize("A3")}
                    >
                      <i className="fa-regular fa-file" />
                      <h2>A3</h2>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center">
                    {/* BLANK LAYOUT */}
                    <div
                      className="layout-card mr-5 px-5 py-5 shadow-lg"
                      onClick={() => applyCanvasSize("16:9")}
                    >
                      <i className="fa-regular fa-file" />
                      <h2>16:9</h2>
                    </div>

                    {/* UPLOAD BACKGROUND */}
                    <div
                      className="layout-card px-5 py-5 shadow-lg"
                      onClick={() => applyCanvasSize("4:3")}
                    >
                      <i className="fa-regular fa-file" />
                      <h2>4:3</h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {copyToast.visible &&
          createPortal(
            <div
              className={`copy-toast visible`}
              style={{
                top: copyToast.top,
                left: copyToast.left,
              }}
            >
              {copyToast.text || "Copied"}
            </div>,
            document.body,
          )}
      </Container>
    </>
  );
};

export default CertificateDesigner;
